import { Button, Menu, MenuButton, MenuDivider, MenuItem, MenuList, Skeleton } from '@chakra-ui/react'
import PropTypes from 'prop-types'
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import { toastr } from 'react-redux-toastr'
import { useHistory } from 'react-router-dom'
import { fetchAgencies, initializeAgency } from '../../actions/agencies'
import { agencyLogin, agencyViewLogout } from '../../actions/sessions'
import { currentAdminPropTypes } from '../../reducers/adminsReducer'
import { agenciesPropTypes } from '../../reducers/agenciesReducer'
import { currentAgencyPropTypes } from '../../reducers/currentReducer'
import MfSelect from '../general/MfSelect'

/**
 * @typedef {Object} AgenciesControlContext
 * @property {boolean} isLoading - Whether agencies data is currently loading.
 * @property {import('../../reducers/agenciesReducer').Agencies} agencies - List of available agencies.
 * @property {import('../../reducers/currentReducer').CurrentAgency} currentAgency - The currently selected agency.
 * @property {boolean} isOrgAdmin - Whether the current admin has organization-level access.
 * @property {(agency: import('../../reducers/agenciesReducer').Agency) => Promise<void>} handleAgencyLogin - Logs into a specified agency by ID.
 * @property {() => Promise<void>} handleAgencyLogout - Logs out from the current agency view.
 */

/** @type {AgenciesControlContext} */
const initialState = {
  isLoading: false,
  isOrgAdmin: false,
  agencies: [],
  currentAgency: null,
  handleAgencyLogin: async () => {},
  handleAgencyLogout: async () => {}
}

const AgenciesControlContext = createContext(initialState)

/**
 * Hook to use the AgenciesControl context.
 *
 * @returns {AgenciesControlContext} The AgenciesControl context value.
 */
const useAgenciesControl = () => useContext(AgenciesControlContext)

/**
 * Provides the AgenciesControl context to its children.
 *
 * @param {Object} props
 * @param {import('../../reducers/agenciesReducer').Agencies} props.agencies - List of agencies from Redux state.
 * @param {import('../../reducers/adminsReducer').CurrentAdmin} props.currentAdmin - Current admin details from Redux state.
 * @param {import('../../reducers/currentReducer').CurrentAgency} props.currentAgency - Current agency details from Redux state.
 * @param {import('../../actions/sessions').agencyLogin} props.agencyLogin - Action to log in to an agency.
 * @param {import('../../actions/sessions').agencyViewLogout} props.agencyViewLogout - Action to log out from agency view.
 * @param {import('../../actions/agencies').fetchAgencies} props.fetchAgencies - Action to fetch agencies.
 * @param {import('../../actions/agencies').initializeAgency} props.initializeAgency - Action to initialize an agency.
 * @param {React.ReactNode} props.children - The child components to render.
 *
 * @returns {JSX.Element} The AgenciesControlProvider component.
 */
function AgenciesControlProvider({
  agencies,
  currentAdmin,
  currentAgency,
  agencyLogin,
  agencyViewLogout,
  fetchAgencies,
  initializeAgency,
  children
}) {
  const history = useHistory()

  /** @type {[AgenciesControlContext['state'], React.Dispatch<React.SetStateAction<AgenciesControlContext['state']>>]} */
  const [isLoading, setIsLoading] = useState(false)

  /** @type {AgenciesControlContext['isOrgAdmin']} */
  const isOrgAdmin = useMemo(
    () => currentAdmin?.organization_admins?.length > 0,
    [currentAdmin?.organization_admins?.length]
  )

  /**
   * Fetches the list of agencies for an organization admin.
   *
   * @returns {Promise<void>} Resolves when agencies are fetched or fails with an error.
   */
  const populateAgencies = useCallback(async () => {
    if (!isOrgAdmin || !currentAdmin?.id) return

    try {
      setIsLoading(true)

      await fetchAgencies()
    } catch (err) {
      toastr.error(err?.message ?? 'There was an error fetching agencies')

      if (err?.response?.status === 401) history.push('/login')
    } finally {
      setIsLoading(false)
    }
  }, [currentAdmin?.id, fetchAgencies, history, isOrgAdmin])

  /**
   * Logs into the specified agency.
   *
   * @param {number} agencyId - The ID of the agency to log into.
   * @returns {Promise<void>} Resolves when login is successful or fails with an error.
   */
  const handleAgencyLogin = useCallback(
    async agency => {
      try {
        setIsLoading(true)

        await agencyLogin(agency.id)
        await initializeAgency()

        history.push('/dashboard')
      } catch (err) {
        toastr.error(err?.message ?? 'There was an error logging in to the agency')
      } finally {
        setIsLoading(false)
      }
    },
    [agencyLogin, initializeAgency, history]
  )

  /**
   * Logs out from the current agency view.
   *
   * @returns {Promise<void>} Resolves when logout is successful or fails with an error.
   */
  const handleAgencyLogout = useCallback(async () => {
    try {
      setIsLoading(true)

      await agencyViewLogout()

      history.push('/')
    } catch (err) {
      toastr.error(err?.message ?? 'There was an error logging out of the agency')
    } finally {
      setIsLoading(false)
    }
  }, [agencyViewLogout, history])

  /** @type {AgenciesControlContext} */
  const state = useMemo(
    () => ({ isLoading, isOrgAdmin, agencies, currentAgency, handleAgencyLogin, handleAgencyLogout }),
    [agencies, currentAgency, handleAgencyLogin, handleAgencyLogout, isLoading, isOrgAdmin]
  )

  useEffect(() => {
    populateAgencies()
  }, [populateAgencies])

  return <AgenciesControlContext.Provider value={state}>{children}</AgenciesControlContext.Provider>
}

AgenciesControlProvider.propTypes = {
  agencies: agenciesPropTypes,
  currentAgency: currentAgencyPropTypes,
  currentAdmin: currentAdminPropTypes,
  agencyLogin: PropTypes.func,
  fetchAgencies: PropTypes.func,
  agencyViewLogout: PropTypes.func,
  initializeAgency: PropTypes.func,
  children: PropTypes.node
}

export const AgenciesControl = connect(
  state => ({
    agencies: state.agencies.agencies,
    currentAgency: state.current.agency,
    currentAdmin: state.admins.current_admin
  }),
  { agencyLogin, agencyViewLogout, fetchAgencies, initializeAgency } // mapDispatchToProps
)(AgenciesControlProvider)

/**
 * Dropdown component for selecting an agency.
 *
 * @param {Object} props - Props passed to the dropdown.
 * @returns {JSX.Element} The dropdown component for selecting an agency.
 */
export function AgenciesDropdown({ badge, logout, ...props }) {
  const { isLoading, agencies, currentAgency, isOrgAdmin, handleAgencyLogin, handleAgencyLogout } = useAgenciesControl()

  const sortedAgencies = useMemo(() => {
    if (!agencies || agencies.length === 0) return []

    return [...agencies].sort((a, b) => {
      if ((a?.name ?? a?.agency_name) < (b?.name ?? b?.agency_name)) {
        return -1
      }
      if ((a?.name ?? a?.agency_name) > (b?.name ?? b?.agency_name)) {
        return 1
      }
      return 0
    })
  }, [agencies])

  if (!isOrgAdmin) return <></>

  if (badge)
    return (
      <Skeleton isLoaded={!isLoading}>
        <Menu size="sm">
          <MenuButton as={Button} colorScheme="primary" variant="outline" size="xs" aria-label="Open Menu" {...props}>
            {currentAgency?.name ?? 'Select Agency'}
          </MenuButton>
          <MenuList>
            {sortedAgencies.map(agency => (
              <MenuItem key={agency.id} onClick={() => handleAgencyLogin(agency)} aria-label="Select Agency">
                {agency.agency_name ?? agency.name}
              </MenuItem>
            ))}
            {logout && currentAgency?.id && (
              <>
                <MenuDivider />
                <MenuItem as={Button} size="sm" colorScheme="error" onClick={handleAgencyLogout}>
                  Exit Agency View
                </MenuItem>
              </>
            )}
          </MenuList>
        </Menu>
      </Skeleton>
    )

  return (
    <div {...props}>
      <MfSelect
        placeholder={isLoading ? 'Loading agencies...' : 'Select an agency'}
        options={agencies}
        getOptionValue={option => option.id}
        getOptionLabel={option => option.agency_name ?? option.name}
        value={currentAgency ?? null}
        onChange={handleAgencyLogin}
      />
    </div>
  )
}

AgenciesDropdown.propTypes = {
  logout: PropTypes.bool,
  badge: PropTypes.bool
}

/**
 * Button component for logging out of the current agency view.
 *
 * @param {Object} props - Props passed to the button.
 * @returns {JSX.Element} The logout button for exiting agency view.
 */
export const AgenciesLogoutButton = props => {
  const { currentAgency, isOrgAdmin, handleAgencyLogout } = useAgenciesControl()

  if (!isOrgAdmin || !currentAgency?.id) return <></>

  return (
    <Button colorScheme="neutral" {...props} onClick={handleAgencyLogout}>
      Exit Agency View
    </Button>
  )
}

/**
 * Main AgenciesControl component with subcomponents for Dropdown and LogoutButton.
 *
 * @type {Object} The AgenciesControl component with `Dropdown` and `LogoutButton` as subcomponents.
 * @property {typeof AgenciesDropdown} Dropdown - Dropdown component for selecting an agency.
 * @property {typeof AgenciesLogoutButton} LogoutButton - Button component for logging out of the current agency view.
 */
export default Object.assign(AgenciesControl, { Dropdown: AgenciesDropdown, LogoutButton: AgenciesLogoutButton })
